// formatUserName — render a user-friendly label for any UserSummary-shaped
// payload. Never falls back to a UUID slice: leaking raw IDs to end users
// hurts UX (the operator sees "019dd65c" instead of "Alice Nguyen") and
// masks data quality issues (BE not embedding users / referenced user
// deleted) behind a string that *looks* intentional.
//
// Fallback chain:
//   "First Last" → "First" / "Last" → username → email → opts.fallback.
//
// Use this everywhere a user reference is rendered. The opts.fallback should
// be a localized string supplied by the caller (e.g. t("meta.unknown_user")).
interface FormatUserNameOptions {
  fallback: string;
}

export interface FormatUserNameInput {
  id?: string | null;
  email?: string | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

export function formatUserName(
  user: FormatUserNameInput | null | undefined,
  opts: FormatUserNameOptions,
): string {
  if (!user) return opts.fallback;
  const first = (user.firstName ?? "").trim();
  const last = (user.lastName ?? "").trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  if (user.username) return user.username;
  if (user.email) return user.email;
  return opts.fallback;
}

export function formatUserIdentifier(
  user: FormatUserNameInput | null | undefined,
  opts: FormatUserNameOptions,
): string {
  if (!user) return opts.fallback;
  if (user.username) return user.username;
  if (user.email) return user.email;
  return formatUserName(user, opts);
}
